import React from 'react';
import { PageHero, Section, CardLayout, Card, CtaButton } from '../components/PageLayout';
import { restaurantImages, heroImages, generalImages } from '../constants/imagePlaceholders';
import { FaClock, FaWineGlassAlt, FaLeaf } from 'react-icons/fa';
import ClickableImage from '../components/ClickableImage';
import SEO from '../components/SEO';

const RestaurantPage: React.FC = () => {
  return (
    <div className="font-body">
      <SEO 
        title="Restaurant"
        description="Restaurant le Moulin Tala : cuisine simple du Morvan, poissons d'eau douce, produits locaux et terrasse."
        canonicalUrl="https://lemoulintala.fr/restaurant"
        keywords="restaurant morvan, cuisine locale, poisson eau douce, brassy, terrasse restaurant"
      />

      {/* Hero Banner */}
      <PageHero 
        title="Restaurant"
        subtitle="Une cuisine simple et conviviale au cœur du Morvan"
        backgroundImage={heroImages.restaurant}
      />

      {/* Introduction */}
      <Section 
        title="Notre cuisine"
        subtitle="Vous y trouverez une cuisine simple où l'on vous propose par exemple : assiette de truite fumée, charcuterie, truites aux amandes, tête de veau, et autres viandes."
        background="light"
        textAlign="center"
      >
        <p className="text-lg max-w-4xl mx-auto">
          Venez découvrir nos spécialités dans une ambiance conviviale et chaleureuse.
        </p>
      </Section>

      {/* Restaurant Images Section */}
      <Section paddingY="16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ClickableImage 
            src={restaurantImages.interior} 
            alt="Intérieur du restaurant" 
            title="Notre salle de restaurant"
            description="Notre salle, simple et accueillante." 
            aspectRatio="4/3"
            className="shadow-md"
          />
          <ClickableImage 
            src={restaurantImages.terrace} 
            alt="Terrasse du restaurant"
            title="Notre terrasse ombragée"
            description="Mangez dehors, en pleine nature."  
            aspectRatio="4/3"
            className="shadow-md"
          />
          <ClickableImage 
            src={restaurantImages.terrace2} 
            alt="Vue sur la terrasse"
            title="Une autre vue de notre terrasse"
            description="Un coin sympa pour se détendre."  
            aspectRatio="4/3"
            className="shadow-md"
          />
        </div>
      </Section>

      {/* Restaurant */}
      <Section background="light" paddingY="16">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          <div>
            <ClickableImage 
              src={restaurantImages.terrace3} 
              alt="Notre terrasse" 
              title="Notre terrasse ensoleillée"
              description="Un bon endroit pour manger au calme."
              className="rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h2 className="text-3xl font-bold mb-4 text-primary font-heading">Ambiance Conviviale</h2>
            <p className="text-lg mb-4">
            Au Moulin Tala, nous vous accueillons comme à la maison ! Notre équipe met tout en œuvre pour vous offrir un moment agréable dans un cadre authentique.
            </p>
            <p className="mb-4">
            Notre spécialité ? La truite aux amandes, pêchée par nos soins à la demande. Mais vous trouverez aussi d'autres plats régionaux pour satisfaire vos envies.
            </p>
            <p className="italic">
              "Notre priorité est que vous passiez un bon moment en dégustant une cuisine simple et savoureuse dans un cadre naturel."
            </p>
          </div>
        </div>
      </Section>

      {/* La Terrasse */}
      <Section paddingY="16">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-primary text-center font-heading">Notre Terrasse</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <ClickableImage 
              src={restaurantImages.terrace4} 
              alt="Terrasse en été" 
              title="Notre terrasse en été"
              description="Un coin à l'ombre pour les beaux jours."
              className="rounded-lg shadow-md h-full"
              aspectRatio="3/4"
            />
            <ClickableImage 
              src={generalImages.vuePortrait1} 
              alt="Vue depuis la terrasse" 
              title="Vue depuis notre terrasse"
              description="Profitez de la vue en mangeant."
              className="rounded-lg shadow-md h-full"
              aspectRatio="3/4"
            />
            <ClickableImage 
              src={generalImages.vuePortrait2} 
              alt="Environnement du restaurant" 
              title="L'environnement de restaurant"
              description="Le cadre naturel du Morvan."
              className="rounded-lg shadow-md h-full"
              aspectRatio="3/4"
            />
          </div>
          <p className="text-center mt-8">
            Aux beaux jours, profitez de notre terrasse ombragée avec vue sur la nature. 
            Un endroit idéal pour manger tranquillement après une journée de pêche ou de promenade.
          </p>
        </div>
      </Section>

      {/* Informations */}
      <Section title="Informations Pratiques" textAlign="center" paddingY="16">
        <Card className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-primary font-heading">Horaires</h3>
              <p className="text-sm mb-3">Ouvert du premier week-end de Mars au premier week-end de Novembre</p>
              <ul className="space-y-2">
                <li className="flex justify-between">
                  <span className="font-medium">Lundi - Mardi</span>
                  <span>Fermé</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Mercredi - Dimanche</span>
                  <span>12h-14h uniquement</span>
                </li>
                <li className="flex justify-between text-accent">
                  <span className="font-medium">Juillet - Août</span>
                  <span>Ouvert tous les jours</span>
                </li>
              </ul>
              <p className="text-xs mt-3 italic">Période d'ouverture : du 14 juillet au dernier week-end d'août ainsi que les jours feriés</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 text-primary font-heading">Réservations</h3>
              <p className="mb-2">
              Pensez à réserver pour être sûr d'avoir votre table.
              </p>
              <p className="font-bold">
                Téléphone: 03 86 22 22 61
              </p>
            </div>
          </div>
        </Card>
      </Section>

      {/* CTA */}
      <Section background="primary" textAlign="center" paddingY="12">
        <h2 className="text-3xl font-bold mb-6 text-white font-heading">Envie de découvrir notre cuisine simple ?</h2>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <CtaButton to="/contact" type="accent">
            Réserver une table
          </CtaButton>
          <CtaButton to="/galerie" type="outline">
            Voir la galerie
          </CtaButton>
        </div>
      </Section>
    </div>
  );
};

export default RestaurantPage; 